export const COOKING_OPTION_VALUES = ["grilling", "oven", "pot"]

export const COOKING_OPTION_LABELS = {
  grilling: "Grilling",
  oven: "Oven",
  pot: "Pot",
}

export const NET_WEIGHT_UNITS = [
  { value: "g", label: "g" },
  { value: "kg", label: "kg" },
  { value: "ml", label: "ml" },
  { value: "L", label: "L" },
]

export const STORAGE_TEMPERATURE_MAX_LENGTH = 50

const STORAGE_TEMPERATURE_PATTERN = /^[\p{L}\p{N}\s-]+$/u

const hasSingleLeadingMinus = text => {
  const minusCount = (text.match(/-/g) || []).length
  if (minusCount === 0) return true
  if (minusCount > 1) return false
  return /(^|[^\p{N}])-\p{N}/u.test(text)
}

export const getStorageTemperatureError = (value, t) => {
  const text = value == null ? "" : String(value).trim()
  if (!text) return ""
  if (text.length > STORAGE_TEMPERATURE_MAX_LENGTH) {
    return t("Storage temperature cannot exceed {{max}} characters.", {
      max: STORAGE_TEMPERATURE_MAX_LENGTH,
    })
  }
  if (!STORAGE_TEMPERATURE_PATTERN.test(text)) {
    return t(
      "Storage temperature can only include letters, numbers, spaces, and a minus sign."
    )
  }
  if (!hasSingleLeadingMinus(text)) {
    return t("Please enter a valid negative number.")
  }
  return ""
}

export const defaultItemExtendedFields = {
  images: [],
  netWeight: { value: 0, unit: "g" },
  storageTemperature: "",
  cookingOptions: [],
  relatedProducts: [],
}

export const buildImagesFromItemDetails = (details = {}) => {
  if (Array.isArray(details.images) && details.images.length > 0) {
    return details.images.map(img => ({
      imageUrl: img.imageUrl || "",
      imageUrlType: img.imageUrlType || "URL",
    }))
  }
  const images = []
  if (details.imageUrl) {
    images.push({
      imageUrl: details.imageUrl,
      imageUrlType: details.imageUrlType || "URL",
    })
  }
  if (Array.isArray(details.galleryImages)) {
    details.galleryImages.forEach(url => {
      if (url && !images.some(img => img.imageUrl === url)) {
        images.push({ imageUrl: url, imageUrlType: "URL" })
      }
    })
  }
  return images
}

/** Other images only (excludes primary image). */
export const buildOtherImagesFromItemDetails = (details = {}) => {
  const primaryUrl = details.imageUrl || ""
  if (Array.isArray(details.images) && details.images.length > 1) {
    return details.images.slice(1).map(img => ({
      imageUrl: img.imageUrl || "",
      imageUrlType: img.imageUrlType || "URL",
      imageFile: null,
    }))
  }
  const all = buildImagesFromItemDetails(details)
  return all
    .filter(img => img.imageUrl && img.imageUrl !== primaryUrl)
    .map(img => ({ ...img, imageFile: null }))
}

export const buildCookingOptionsSelectValue = (options = [], t) =>
  (Array.isArray(options) ? options : []).map(option => {
    const value = typeof option === "string" ? option : option?.value
    return {
      value,
      label: t(COOKING_OPTION_LABELS[value] || value),
    }
  })

const normalizeCookingOptions = cookingOptions =>
  (Array.isArray(cookingOptions) ? cookingOptions : [])
    .map(option => (typeof option === "string" ? option : option?.value))
    .filter(Boolean)

export const appendItemExtendedFields = (formData, extended, { primaryImage } = {}) => {
  if (!extended) return formData

  const otherImages = extended.images || []
  const imagesPayload = []

  if (primaryImage) {
    imagesPayload.push({
      imageUrl: primaryImage.imageUrl || "",
      imageUrlType: primaryImage.imageUrlType || "URL",
    })
  }

  otherImages.forEach((img, index) => {
    const imageUrlType = img.imageUrlType || "URL"
    if (imageUrlType === "UPLOAD" && img.imageFile) {
      formData.append(`otherImage_${index}`, img.imageFile)
      imagesPayload.push({ imageUrl: "", imageUrlType: "UPLOAD" })
    } else if (imageUrlType === "UPLOAD" && img.imageUrl) {
      imagesPayload.push({
        imageUrl: img.imageUrl,
        imageUrlType: "UPLOAD",
      })
    } else if (img.imageUrl) {
      imagesPayload.push({
        imageUrl: img.imageUrl,
        imageUrlType: imageUrlType,
      })
    }
  })

  if (imagesPayload.length > 0) {
    formData.append("images", JSON.stringify(imagesPayload))
  }

  formData.append(
    "netWeight",
    JSON.stringify({
      value: Number(extended.netWeight?.value || 0),
      unit: extended.netWeight?.unit || "g",
    })
  )

  const storageTemperature = String(extended.storageTemperature ?? "").trim()
  if (storageTemperature) {
    formData.append("storageTemperature", storageTemperature)
  }

  const cookingOptions = normalizeCookingOptions(extended.cookingOptions)
  formData.append("cookingOptions", JSON.stringify(cookingOptions))

  const relatedIds = (extended.relatedProducts || []).map(item =>
    typeof item === "string" ? item : item?.value || item?._id
  )
  formData.append("relatedProducts", JSON.stringify(relatedIds.filter(Boolean)))

  return formData
}
